import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, ArrowRightIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { listPatientsPage, akteAusSimed, patientenAuftraege, fehlgeschlageneErneut, type Patient, type Bereich } from '../api/unimedica';
import { useBranding } from '../contexts/BrandingContext';

/* Zwei getrennte Ansichten statt einer Mischliste (Port aus patienten/+page.svelte).
 *
 * Von 11.366 Patienten liegen erst gut hundert Akten vor. In einer gemeinsamen
 * Liste verschwinden die vorhandenen zwischen Tausenden noch nicht geholten.
 * Deshalb zwei Reiter, jeder mit eigener Seite und eigenem Zaehler.
 *
 * BEWUSST sessionStorage fuer den Sofortstart: die Liste enthaelt Namen,
 * Geburtsdaten und AHV-Nummern. Im sessionStorage lebt sie nur, solange der
 * Tab offen ist — auf einem Praxis-iPad mit mehreren Personen entscheidend.
 */
const SPEICHER = 'powercare.patienten.importiert';

const praxisId = (pid: string) => String(pid).split('_')[0];
const fmtDob = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso);
};
/** „neuer Versuch um 16:40" bzw. „morgen um 09:12" — ein Fehlschlag ist kein Endzustand mehr:
 *  das System reiht den Auftrag von selbst wieder ein (simed_queue.WIEDERANLAUF). */
function naechsterVersuchText(sek?: number): string {
  if (!sek) return '';
  const d = new Date(sek * 1000);
  const jetzt = new Date();
  const uhr = d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  if (d.getTime() <= jetzt.getTime()) return 'neuer Versuch läuft an';
  const gleicherTag = d.toDateString() === jetzt.toDateString();
  const morgen = new Date(jetzt.getTime() + 86400000).toDateString() === d.toDateString();
  return gleicherTag ? `neuer Versuch um ${uhr}` : morgen ? `neuer Versuch morgen um ${uhr}`
                     : `neuer Versuch am ${d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })}`;
}

const auftragText = (s?: string) =>
  s === 'laeuft' ? 'Wird importiert …' : s === 'wartend' ? 'Wartend' : s === 'fehler' ? 'Fehlgeschlagen' : '';

const RASTER = 'sm:grid-cols-[84px_1.3fr_1fr_104px_150px_140px]';
const RASTER_AKTEN = 'sm:grid-cols-[84px_minmax(0,1.3fr)_minmax(0,1fr)_104px_150px_164px_150px]';

/** Zuletzt aus siMed geholt: „heute 08:14“, „gestern 21:03“, sonst Datum. */
function zuletztText(sek?: number | null): string {
  if (!sek) return '—';
  const d = new Date(sek * 1000), h = new Date();
  const uhr = d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === h.toDateString()) return `heute ${uhr}`;
  if (d.toDateString() === new Date(h.getTime() - 86400000).toDateString()) return `gestern ${uhr}`;
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

type Sortierung = { feld: string; richtung: 'auf' | 'ab' } | null;
// Erster Klick: Namen A→Z, Zahlen und Daten neueste/groesste zuerst; zweiter Klick dreht um, dritter hebt auf.
const ERSTE_RICHTUNG: Record<string, 'auf' | 'ab'> = {
  id: 'auf', nachname: 'auf', vorname: 'auf', geburtsdatum: 'auf', ahv: 'auf', aktualisiert: 'ab', dokumente: 'ab',
};

export default function Patienten() {
  const [bereich, setBereich] = useState<Bereich>('importiert');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [aktualisiert, setAktualisiert] = useState(false);
  const [query, setQuery] = useState('');
  const [zahlImportiert, setZahlImportiert] = useState(0);
  const [zahlOffen, setZahlOffen] = useState(0);
  const [sammelLaeuft, setSammelLaeuft] = useState(false);
  const [sammelMeldung, setSammelMeldung] = useState<string | null>(null);
  const [gesamt, setGesamt] = useState(0);
  const [hatMehr, setHatMehr] = useState(false);
  const [auftraege, setAuftraege] = useState<Record<string, string>>({});
  const [holeLaeuft, setHoleLaeuft] = useState<Record<string, boolean>>({});
  const simed = !!useBranding().branding.funktionen?.simed;
  const [sortierung, setSortierung] = useState<Sortierung>(null);
  const sortRef = useRef<Sortierung>(null);
  sortRef.current = sortierung;
  // Nach einem abgebrochenen Sammel-Export stehen schnell zwei Dutzend Akten auf „Fehlgeschlagen",
  // und der Automatismus versucht es nach zwei Anlaeufen nicht mehr. Ein Knopf fuer alle.
  const fehlerZahl = Object.values(auftraege).filter((s) => s === 'fehler').length;
  const alleErneut = async () => {
    setSammelLaeuft(true); setSammelMeldung(null);
    try {
      const r = await fehlgeschlageneErneut();
      setSammelMeldung(r.erneut === 0 ? 'Nichts zu wiederholen.'
        : `${r.erneut} Import${r.erneut === 1 ? '' : 'e'} erneut angestossen — das dauert einige Minuten.`);
      const { auftraege: a } = await patientenAuftraege();
      const neu: Record<string, string> = {};
      for (const [pid, job] of Object.entries(a ?? {})) neu[pid] = (job as any).status;
      setAuftraege(neu);
    } catch (e: any) { setSammelMeldung(e?.message ?? String(e)); }
    finally { setSammelLaeuft(false); }
  };   // siMed-Anbindung je Mandant (Betreiber-Konsole → Datenquellen)
  const patientsRef = useRef<Patient[]>([]);
  patientsRef.current = patients;

  const merkeAuftraege = (liste: Patient[]) => {
    const neu: Record<string, string> = {};
    for (const p of liste) {
      const st = (p as any).auftrag;
      if (st) neu[praxisId(p.patient_id)] = st;
    }
    setAuftraege(neu);
  };

  /** Liste neu holen — gesucht und portioniert wird auf dem SERVER. */
  const nachladen = useCallback(async (b: Bereich, q: string, anhaengen = false) => {
    const offset = anhaengen ? patientsRef.current.length : 0;
    const s = sortRef.current;
    const d = await listPatientsPage({ bereich: b, q, limit: 50, offset, sort: s?.feld, richtung: s?.richtung });
    const liste = anhaengen ? [...patientsRef.current, ...d.patients] : d.patients;
    setPatients(liste);
    setGesamt(d.gesamt);
    if (d.gesamt_importiert !== undefined) setZahlImportiert(d.gesamt_importiert);
    if (d.gesamt_offen !== undefined) setZahlOffen(d.gesamt_offen);
    setHatMehr(d.hat_mehr);
    merkeAuftraege(liste);
    if (b === 'importiert' && !anhaengen && !q && !s) {
      try { sessionStorage.setItem(SPEICHER, JSON.stringify(d.patients)); } catch { /* egal */ }
    }
  }, []);

  // Sofortstart aus dem Zwischenspeicher, dann frisch laden.
  useEffect(() => {
    try {
      const roh = sessionStorage.getItem(SPEICHER);
      if (roh) {
        const g = JSON.parse(roh);
        if (Array.isArray(g) && g.length) { setPatients(g); setLoading(false); setAktualisiert(true); }
      }
    } catch { /* kaputter Speicher darf die Seite nicht aufhalten */ }
    nachladen('importiert', '').finally(() => { setLoading(false); setAktualisiert(false); });
  }, [nachladen]);

  // Sucheingabe entprellen: sonst eine Anfrage je Tastendruck.
  const erstesRendern = useRef(true);
  useEffect(() => {
    if (erstesRendern.current) { erstesRendern.current = false; return; }
    const t = setTimeout(() => { void nachladen(bereich, query); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const wechsle = async (neu: Bereich) => {
    if (neu === bereich) return;
    setBereich(neu);
    setPatients([]);
    setLoading(true);
    await nachladen(neu, query);
    setLoading(false);
  };

  const akteHolen = async (p: Patient, ev: React.MouseEvent) => {
    ev.preventDefault(); ev.stopPropagation();
    const code = praxisId(p.patient_id);
    setHoleLaeuft((h) => ({ ...h, [code]: true }));
    try {
      await akteAusSimed(code);
      setAuftraege((a) => ({ ...a, [code]: 'wartend' }));
    } finally {
      setHoleLaeuft((h) => ({ ...h, [code]: false }));
    }
  };

  // Solange ein Auftrag laeuft, den Zustand nachfuehren — aber NUR dann.
  useEffect(() => {
    const offen = Object.values(auftraege).some((s) => s === 'laeuft' || s === 'wartend');
    if (!offen) return;
    const t = setInterval(async () => {
      try {
        const { auftraege: a } = await patientenAuftraege();
        const neu: Record<string, string> = {};
        for (const [pid, job] of Object.entries(a ?? {})) neu[pid] = (job as any).status;
        setAuftraege(neu);
        if (Object.values(neu).some((s) => s === 'fertig')) await nachladen(bereich, query);
      } catch { /* beim naechsten Mal wieder */ }
    }, 5000);
    return () => clearInterval(t);
  }, [auftraege, bereich, query, nachladen]);

  const sammelLeiste = simed && bereich === 'offen' && fehlerZahl > 0 ? (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2">
      <span className="text-[12.5px] text-amber-900">
        {fehlerZahl} Import{fehlerZahl === 1 ? '' : 'e'} zuletzt fehlgeschlagen. Sie werden von selbst erneut
        versucht — mit wachsendem Abstand, bis es klappt.
      </span>
      <button onClick={() => void alleErneut()} disabled={sammelLaeuft} className="btn-primary shrink-0 text-xs">
        {sammelLaeuft ? 'Wird angestossen …' : 'Alle sofort versuchen'}
      </button>
    </div>
  ) : null;

  const sortiere = (feld: string) => {
    const s = sortRef.current;
    const neu: Sortierung = !s || s.feld !== feld ? { feld, richtung: ERSTE_RICHTUNG[feld] ?? 'auf' }
      : s.richtung === ERSTE_RICHTUNG[feld] ? { feld, richtung: s.richtung === 'auf' ? 'ab' : 'auf' } : null;
    sortRef.current = neu; setSortierung(neu);
    void nachladen(bereich, query);
  };
  const akten = bereich === 'importiert';
  const raster = akten ? RASTER_AKTEN : RASTER;
  const kopf = (feld: string, label: string, rechts = false) => {
    const aktiv = sortierung?.feld === feld;
    const Pfeil = sortierung?.richtung === 'ab' ? ChevronDownIcon : ChevronUpIcon;
    return (
      <button type="button" onClick={() => sortiere(feld)}
        className={`inline-flex min-w-0 items-center gap-1 text-left uppercase tracking-wider hover:text-ps-dark-700 ${rechts ? 'justify-self-end' : ''} ${aktiv ? 'text-ps-dark-700' : ''}`}
        title="Sortieren">
        {label}{aktiv && <Pfeil className="h-3 w-3" />}
      </button>
    );
  };

  const reiter: { id: Bereich; label: string; n: number }[] = [
    { id: 'importiert', label: 'Akte vorhanden', n: zahlImportiert },
    { id: 'offen', label: 'Noch nicht importiert', n: zahlOffen },
  ];

  return (
    <div className="space-y-4">
      <div className="card overflow-hidden p-0">
        {/* Kopf: Titel + Suche */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <h1 className="page-h1">
            Patienten
            {aktualisiert && <span className="ml-1 text-xs font-normal text-ps-dark-400">wird aktualisiert …</span>}
          </h1>
          <div className="relative w-full sm:w-72">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ps-dark-400" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, Geburtsdatum, ID oder AHV-Nr. …" className="input-field pl-9 !text-sm" />
          </div>
        </div>

        {/* Zwei Reiter */}
        <div className="flex gap-1 border-b border-gray-200 px-4">
          {reiter.map((r) => (
            <button key={r.id} onClick={() => void wechsle(r.id)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${bereich === r.id
                ? 'border-primary-500 font-medium text-ps-dark-900'
                : 'border-transparent text-ps-dark-400 hover:text-ps-dark-700'}`}>
              {r.label}
              <span className="numerical ml-1 text-xs text-ps-dark-400">({r.n.toLocaleString('de-CH')})</span>
            </button>
          ))}
        </div>

        {sammelLeiste && <div className="px-4 pt-3">{sammelLeiste}</div>}
        {sammelMeldung && (
          <div className="mx-4 mt-3 flex items-start justify-between gap-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-800">
            <span>{sammelMeldung}</span>
            <button onClick={() => setSammelMeldung(null)} className="shrink-0 text-xs underline">schliessen</button>
          </div>
        )}

        {loading ? (
          <div className="divide-y divide-gray-200">
            {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="px-4 py-3"><div className="shimmer h-5 rounded" /></div>)}
          </div>
        ) : patients.length === 0 ? (
          <div className="py-8 text-center text-sm text-ps-dark-400">
            {query ? (
              <>Keine Treffer für „{query}"
                {bereich === 'importiert' && zahlOffen > 0 && (
                  <> — <button className="underline" onClick={() => void wechsle('offen')}>{zahlOffen.toLocaleString('de-CH')} bei den noch nicht importierten</button></>
                )}
              </>
            ) : bereich === 'importiert' ? 'Noch keine Patientenakten importiert.' : 'Alle Patienten der Stammliste sind importiert.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className={`hidden ${raster} items-center gap-3 bg-gray-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-ps-dark-400 sm:grid`}>
              {kopf('id', 'ID')}{kopf('nachname', 'Nachname')}{kopf('vorname', 'Vorname')}{kopf('geburtsdatum', 'Geburtsdatum')}{kopf('ahv', 'AHV-Nummer')}
              {akten && <span className="whitespace-nowrap">{kopf('aktualisiert', 'Zuletzt aktualisiert')}</span>}
              {akten ? kopf('dokumente', 'Dokumente', true) : <span />}
            </div>
            <div className="divide-y divide-gray-200">
              {patients.map((p) => {
                const code = praxisId(p.patient_id);
                const offen = p.importiert === false;
                const inhalt = (
                  <>
                    <span className="numerical hidden text-sm text-ps-dark-500 sm:block">{code}</span>
                    <div className="min-w-0 sm:hidden">
                      <div className="truncate text-sm font-medium text-ps-dark-900">{p.nachname ?? ''}{p.vorname ? ', ' + p.vorname : ''}</div>
                      <div className="numerical mt-0.5 text-xs text-ps-dark-400">{code} · geb. {fmtDob(p.geburtsdatum)}{p.ahv ? ' · ' + p.ahv : ''}{akten && p.simed_zuletzt ? ' · akt. ' + zuletztText(p.simed_zuletzt) : ''}</div>
                    </div>
                    <span className="hidden truncate text-sm font-medium text-ps-dark-900 sm:block">{p.nachname ?? '—'}</span>
                    <span className="hidden truncate text-sm text-ps-dark-700 sm:block">{p.vorname ?? '—'}</span>
                    <span className="numerical hidden text-sm text-ps-dark-700 sm:block">{fmtDob(p.geburtsdatum)}</span>
                    <span className={`numerical hidden text-sm sm:block ${p.ahv ? 'text-ps-dark-700' : 'text-ps-dark-400'}`}>{p.ahv || '—'}</span>
                    {akten && <span className={`numerical hidden text-sm sm:block ${p.simed_zuletzt ? 'text-ps-dark-700' : 'text-ps-dark-400'}`}
                      title={p.simed_zuletzt ? 'Letzte erfolgreiche Aktualisierung aus siMed' : 'Seit der Umstellung auf PowerCare (08.09.2026) noch nicht neu aus siMed geholt'}>{zuletztText(p.simed_zuletzt)}</span>}
                    <span className="text-right text-sm">
                      {offen ? (
                        <>
                        {p.tentative && <span className="badge badge-warn mr-2" title="Vorläufige Akte (HL7-/Mail-Daten liegen vor) — die Vollakte wird automatisch aus siMed nachgeholt">vorläufig</span>}
                        {auftraege[code] === 'fehler' ? (
                          <>
                            <span className="text-xs text-amber-700" title={(p as any).auftrag_meldung ?? ''}>
                              Fehlgeschlagen
                              {naechsterVersuchText((p as any).auftrag_naechster_versuch) && (
                                <span className="block text-[11px] text-ps-dark-400">
                                  {naechsterVersuchText((p as any).auftrag_naechster_versuch)}
                                </span>
                              )}
                            </span>
                            {simed && <button onClick={(e) => akteHolen(p, e)} disabled={holeLaeuft[code]} className="btn-ghost ml-1 text-xs">Jetzt</button>}
                          </>
                        ) : auftragText(auftraege[code]) ? (
                          <span className="text-xs text-ps-dark-500">{auftragText(auftraege[code])}</span>
                        ) : simed ? (
                          <button onClick={(e) => akteHolen(p, e)} disabled={holeLaeuft[code]} className="btn-ghost whitespace-nowrap text-xs">
                            {holeLaeuft[code] ? '…' : 'Aus siMed holen'}
                          </button>
                        ) : null}
                        </>
                      ) : (
                        <>
                          {p.tentative && <span className="badge badge-warn mr-2" title="Vorläufige Akte aus der Stammliste (z.B. HL7-Laborwerte) — siMed-Import ausstehend">vorläufig</span>}
                          {/* Christian 08.10.2026: bestehende Akte aus siMed aktualisieren — holt nur Neues und Geaendertes
                              (Export-Ordner dauerhaft, Importer vergleicht den Inhalt). Gleicher Auftrag wie „Aus siMed holen“. */}
                          {simed && (auftraege[code] === 'laeuft' || auftraege[code] === 'wartend' ? (
                            <span className="mr-2 text-xs text-ps-dark-500">{auftraege[code] === 'laeuft' ? 'Wird aktualisiert …' : 'Aktualisierung wartet'}</span>
                          ) : (
                            <>
                              {auftraege[code] === 'fehler' && <span className="mr-1 text-xs text-amber-700" title={(p as any).auftrag_meldung ?? ''}>Aktualisierung fehlgeschlagen</span>}
                              <button onClick={(e) => akteHolen(p, e)} disabled={holeLaeuft[code]} className="btn-ghost mr-2 whitespace-nowrap text-xs" title="Patientenakte aus siMed aktualisieren — holt nur Neues und Geändertes">
                                {holeLaeuft[code] ? '…' : 'Aus siMed aktualisieren'}
                              </button>
                            </>
                          ))}
                          <span className="numerical font-semibold">{p.doc_count ?? 0}</span>
                          <span className="ml-1 hidden text-xs text-ps-dark-400 sm:inline">{(p.doc_count ?? 0) === 1 ? 'Dokument' : 'Dokumente'}</span>
                          <ArrowRightIcon className="ml-2 inline h-3.5 w-3.5 text-ps-dark-400 transition-colors group-hover:text-primary-500" />
                        </>
                      )}
                    </span>
                  </>
                );
                const klassen = `group grid grid-cols-[1fr_auto] ${raster} items-center gap-3 px-4 py-2.5 transition-colors`;
                // Vorlaeufige Akten stehen bei den noch nicht importierten, sind aber bereits eine Akte (HL7-Werte) — Zeile bleibt verlinkt
                return offen && !p.tentative
                  ? <div key={p.patient_id} className={klassen}>{inhalt}</div>
                  : <Link key={p.patient_id} to={`/patienten/${encodeURIComponent(p.patient_id)}`} className={`${klassen} hover:bg-gray-50`}>{inhalt}</Link>;
              })}
            </div>
            {hatMehr && (
              <div className="border-t border-gray-200 px-4 py-3 text-center">
                <button onClick={() => void nachladen(bereich, query, true)} className="btn-ghost text-xs">
                  Weitere anzeigen ({(gesamt - patients.length).toLocaleString('de-CH')} übrig)
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
